// src/screens/ReportesAdmin.js
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { db, auth } from '../firebase/config';
import { collection, getDocs } from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import {
  FaArrowLeft,
  FaChartBar,
  FaVideo,
  FaListOl,
  FaUsers,
  FaChevronDown,
  FaChevronUp,
  FaHome,
  FaSignOutAlt,
  FaSearch,
  FaEye,
  FaUserCheck
} from 'react-icons/fa';
import {
  Chart as ChartJS,
  BarElement,
  ArcElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Bar, Pie } from 'react-chartjs-2';

ChartJS.register(
  BarElement,
  ArcElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  ChartDataLabels
);

const APP_PRIMARY = '#b03060';

const ReportesAdmin = () => {
  const navigate = useNavigate();
  const [videos, setVideos] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [paginaVideos, setPaginaVideos] = useState(1);
  const [paginaUsuarios, setPaginaUsuarios] = useState(1);
  const itemsPorPagina = 15;
  const [busquedaVideos, setBusquedaVideos] = useState('');
  const [busquedaUsuarios, setBusquedaUsuarios] = useState('');

  const [mostrarCategorias, setMostrarCategorias] = useState(false);
  const [mostrarTopVideos, setMostrarTopVideos] = useState(false);
  const [mostrarTodosVideos, setMostrarTodosVideos] = useState(false);
  const [mostrarUsuariosActivos, setMostrarUsuariosActivos] = useState(false);

  useEffect(() => {
    const cargarDatos = async () => {
      try {
        const snapVideos = await getDocs(collection(db, 'videos'));
        setVideos(snapVideos.docs.map((d) => ({ id: d.id, ...d.data() })));

        const snapUsuarios = await getDocs(collection(db, 'usuarios'));
        setUsuarios(snapUsuarios.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error('Error al obtener datos:', err);
      }
    };
    cargarDatos();
  }, []);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (err) {
      alert('Error al cerrar sesión: ' + err.message);
    }
  };

  const normalizarTexto = (texto) =>
    texto?.toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '') || '';

  const normalizarCategoria = (cat) => cat?.split('_').slice(3).join(' ') || cat || 'General';

  const obtenerVistasPorCategoria = () => {
    const vistasPorCat = {};
    videos.forEach((v) => {
      const cat = normalizarCategoria(v.categoria);
      vistasPorCat[cat] = (vistasPorCat[cat] || 0) + (v.vistas || 0);
    });
    return vistasPorCat;
  };

  const obtenerVideosOrdenados = () =>
    [...videos]
      .filter((v) => normalizarTexto(v.nombreVisible).includes(normalizarTexto(busquedaVideos)))
      .sort((a, b) => (b.vistas || 0) - (a.vistas || 0));

  const obtenerUsuariosOrdenados = () =>
    [...usuarios]
      .filter((u) => {
        const nombreCompleto = `${u.nombre || ''} ${u.apellido || ''}`;
        return (
          normalizarTexto(nombreCompleto).includes(normalizarTexto(busquedaUsuarios)) ||
          normalizarTexto(u.email).includes(normalizarTexto(busquedaUsuarios))
        );
      })
      .sort((a, b) => (b.accesos || 0) - (a.accesos || 0));

  const vistasPorCategoria = obtenerVistasPorCategoria();
  const videosOrdenados = obtenerVideosOrdenados();
  const top5Videos = [...videos].sort((a, b) => (b.vistas || 0) - (a.vistas || 0)).slice(0, 5);
  const usuariosOrdenados = obtenerUsuariosOrdenados();

  const dataBar = {
    labels: Object.keys(vistasPorCategoria),
    datasets: [
      {
        label: 'Vistas',
        data: Object.values(vistasPorCategoria),
        backgroundColor: APP_PRIMARY,
        borderRadius: 8,
        maxBarThickness: 38
      }
    ]
  };

  const totalVistas = top5Videos.reduce((acc, v) => acc + (v.vistas || 0), 0);
  const dataPie = {
    labels: top5Videos.map((v) => v.nombreVisible || 'Sin título'),
    datasets: [
      {
        data: top5Videos.map((v) => v.vistas || 0),
        backgroundColor: ['#b03060', '#db2777', '#f43f5e', '#fb7185', '#fda4af'],
        borderWidth: 2,
        borderColor: '#ffffff'
      }
    ]
  };

  const optionsBar = {
    indexAxis: 'x',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (context) => ` ${context.parsed.y} vistas`
        }
      },
      datalabels: {
        display: true,
        color: '#ffffff',
        anchor: 'center',
        align: 'center',
        font: { weight: 'bold', size: 11 },
        formatter: (value, context) => {
          const total = context.chart.data.datasets[0].data.reduce((a, b) => a + b, 0);
          return total > 0 ? `${((value / total) * 100).toFixed(1)}%` : '0%';
        }
      }
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: '#64748b', font: { size: 12 } }
      },
      y: {
        beginAtZero: true,
        grid: { color: '#f1f5f9' },
        ticks: { stepSize: 1, color: '#64748b' }
      }
    }
  };

  const optionsPie = {
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          boxWidth: 12,
          color: '#475569',
          font: { size: 11 }
        }
      },
      datalabels: {
        color: '#ffffff',
        font: { weight: 'bold', size: 11 },
        formatter: (value) => (totalVistas > 0 ? `${((value / totalVistas) * 100).toFixed(1)}%` : '0%')
      }
    },
    maintainAspectRatio: false,
    responsive: true
  };

  const videosPaginaActual = videosOrdenados.slice(
    (paginaVideos - 1) * itemsPorPagina,
    paginaVideos * itemsPorPagina
  );
  const usuariosPaginaActual = usuariosOrdenados.slice(
    (paginaUsuarios - 1) * itemsPorPagina,
    paginaUsuarios * itemsPorPagina
  );

  const renderPaginacion = (totalItems, paginaActual, setPagina) => {
    const totalPaginas = Math.ceil(totalItems / itemsPorPagina);
    if (totalPaginas <= 1) return null;
    return (
      <div style={styles.paginacion}>
        {Array.from({ length: totalPaginas }, (_, i) => (
          <button
            key={i}
            onClick={() => setPagina(i + 1)}
            style={{
              ...styles.paginaBtn,
              ...(paginaActual === i + 1 ? styles.paginaActiva : {})
            }}
          >
            {i + 1}
          </button>
        ))}
      </div>
    );
  };

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        {/* Encabezado */}
        <div style={styles.headerRow}>
          <button onClick={() => navigate('/admin')} style={styles.backBtn}>
            <FaArrowLeft size={13} /> Volver al panel
          </button>
          <span style={styles.headerBadge}>Analítica y Reportes</span>
        </div>

        <div style={styles.titleSection}>
          <h1 style={styles.pageTitle}>Reportes Estadísticos</h1>
          <p style={styles.subtitle}>
            Métricas de consumo de contenidos, reproducción de videos y actividad de usuarios
          </p>
        </div>

        {/* Tarjetas Acordeón */}
        <div style={styles.accordionGroup}>
          {/* Acordeón 1: Vistas por Categoría */}
          <div style={styles.accordionCard}>
            <button
              type="button"
              onClick={() => setMostrarCategorias(!mostrarCategorias)}
              style={{
                ...styles.accordionHeader,
                backgroundColor: mostrarCategorias ? '#fdf2f8' : '#ffffff'
              }}
            >
              <div style={styles.headerLeft}>
                <div style={styles.iconCircle}>
                  <FaChartBar size={15} color={APP_PRIMARY} />
                </div>
                <div>
                  <h3 style={styles.sectionTitle}>Vistas por Categoría</h3>
                  <span style={styles.sectionSubtitle}>Distribución del consumo por grupo muscular y plan</span>
                </div>
              </div>
              <div style={styles.chevronWrap}>
                {mostrarCategorias ? <FaChevronUp size={12} color={APP_PRIMARY} /> : <FaChevronDown size={12} color="#94a3b8" />}
              </div>
            </button>

            {mostrarCategorias && (
              <div style={styles.accordionBody}>
                <div style={styles.chartWrapper}>
                  <div style={{ height: 260 }}>
                    <Bar data={dataBar} options={optionsBar} plugins={[ChartDataLabels]} />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Acordeón 2: Top 5 videos más vistos */}
          <div style={styles.accordionCard}>
            <button
              type="button"
              onClick={() => setMostrarTopVideos(!mostrarTopVideos)}
              style={{
                ...styles.accordionHeader,
                backgroundColor: mostrarTopVideos ? '#fdf2f8' : '#ffffff'
              }}
            >
              <div style={styles.headerLeft}>
                <div style={styles.iconCircle}>
                  <FaListOl size={15} color={APP_PRIMARY} />
                </div>
                <div>
                  <h3 style={styles.sectionTitle}>Top 5 Videos Más Vistos</h3>
                  <span style={styles.sectionSubtitle}>Los ejercicios con mayor volumen de reproducción</span>
                </div>
              </div>
              <div style={styles.chevronWrap}>
                {mostrarTopVideos ? <FaChevronUp size={12} color={APP_PRIMARY} /> : <FaChevronDown size={12} color="#94a3b8" />}
              </div>
            </button>

            {mostrarTopVideos && (
              <div style={styles.accordionBody}>
                <div style={styles.pieWrapper}>
                  <div style={{ height: 280, position: 'relative' }}>
                    <Pie data={dataPie} options={optionsPie} plugins={[ChartDataLabels]} />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Acordeón 3: Todos los videos ordenados por vistas */}
          <div style={styles.accordionCard}>
            <button
              type="button"
              onClick={() => setMostrarTodosVideos(!mostrarTodosVideos)}
              style={{
                ...styles.accordionHeader,
                backgroundColor: mostrarTodosVideos ? '#fdf2f8' : '#ffffff'
              }}
            >
              <div style={styles.headerLeft}>
                <div style={styles.iconCircle}>
                  <FaVideo size={15} color={APP_PRIMARY} />
                </div>
                <div>
                  <h3 style={styles.sectionTitle}>Ranking Completo de Videos</h3>
                  <span style={styles.sectionSubtitle}>Lista completa clasificada de mayor a menor reproducción</span>
                </div>
              </div>
              <div style={styles.chevronWrap}>
                {mostrarTodosVideos ? <FaChevronUp size={12} color={APP_PRIMARY} /> : <FaChevronDown size={12} color="#94a3b8" />}
              </div>
            </button>

            {mostrarTodosVideos && (
              <div style={styles.accordionBody}>
                <div style={styles.searchWrapper}>
                  <FaSearch style={styles.searchIcon} />
                  <input
                    type="text"
                    placeholder="Buscar por título de ejercicio..."
                    value={busquedaVideos}
                    onChange={(e) => {
                      setBusquedaVideos(e.target.value);
                      setPaginaVideos(1);
                    }}
                    style={styles.inputBusqueda}
                  />
                </div>

                <div style={styles.tableWrapper}>
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        <th style={styles.th}>Título del Ejercicio</th>
                        <th style={styles.th}>Categoría / Plan</th>
                        <th style={{ ...styles.th, textAlign: 'center' }}>Vistas</th>
                      </tr>
                    </thead>
                    <tbody>
                      {videosPaginaActual.length === 0 ? (
                        <tr>
                          <td colSpan={3} style={styles.emptyRow}>
                            No se encontraron videos coincidentes.
                          </td>
                        </tr>
                      ) : (
                        videosPaginaActual.map((v, idx) => (
                          <tr key={v.id} style={idx % 2 === 0 ? styles.rowPar : styles.rowImpar}>
                            <td style={styles.td}>
                              <strong style={{ color: '#1e293b' }}>{v.nombreVisible || '(Sin nombre)'}</strong>
                            </td>
                            <td style={styles.td}>
                              <span style={styles.catBadge}>{normalizarCategoria(v.categoria)}</span>
                            </td>
                            <td style={{ ...styles.td, textAlign: 'center' }}>
                              <span style={styles.viewsBadge}>
                                <FaEye size={11} /> {v.vistas || 0}
                              </span>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                {renderPaginacion(videosOrdenados.length, paginaVideos, setPaginaVideos)}
              </div>
            )}
          </div>

          {/* Acordeón 4: Usuarios más activos */}
          <div style={styles.accordionCard}>
            <button
              type="button"
              onClick={() => setMostrarUsuariosActivos(!mostrarUsuariosActivos)}
              style={{
                ...styles.accordionHeader,
                backgroundColor: mostrarUsuariosActivos ? '#fdf2f8' : '#ffffff'
              }}
            >
              <div style={styles.headerLeft}>
                <div style={styles.iconCircle}>
                  <FaUsers size={15} color={APP_PRIMARY} />
                </div>
                <div>
                  <h3 style={styles.sectionTitle}>Usuarios Más Activos</h3>
                  <span style={styles.sectionSubtitle}>Ranking de interacción según cantidad de accesos</span>
                </div>
              </div>
              <div style={styles.chevronWrap}>
                {mostrarUsuariosActivos ? <FaChevronUp size={12} color={APP_PRIMARY} /> : <FaChevronDown size={12} color="#94a3b8" />}
              </div>
            </button>

            {mostrarUsuariosActivos && (
              <div style={styles.accordionBody}>
                <div style={styles.searchWrapper}>
                  <FaSearch style={styles.searchIcon} />
                  <input
                    type="text"
                    placeholder="Buscar por nombre o correo..."
                    value={busquedaUsuarios}
                    onChange={(e) => {
                      setBusquedaUsuarios(e.target.value);
                      setPaginaUsuarios(1);
                    }}
                    style={styles.inputBusqueda}
                  />
                </div>

                <div style={styles.tableWrapper}>
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        <th style={styles.th}>Nombre del Alumno</th>
                        <th style={styles.th}>Correo Electrónico</th>
                        <th style={{ ...styles.th, textAlign: 'center' }}>Accesos</th>
                      </tr>
                    </thead>
                    <tbody>
                      {usuariosPaginaActual.length === 0 ? (
                        <tr>
                          <td colSpan={3} style={styles.emptyRow}>
                            No se encontraron usuarios coincidentes.
                          </td>
                        </tr>
                      ) : (
                        usuariosPaginaActual.map((u, idx) => (
                          <tr key={u.id} style={idx % 2 === 0 ? styles.rowPar : styles.rowImpar}>
                            <td style={styles.td}>
                              <strong style={{ color: '#1e293b' }}>
                                {u.nombre} {u.apellido}
                              </strong>
                            </td>
                            <td style={styles.td}>{u.email || '—'}</td>
                            <td style={{ ...styles.td, textAlign: 'center' }}>
                              <span style={styles.accessBadge}>
                                <FaUserCheck size={11} /> {u.accesos || 0}
                              </span>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                {renderPaginacion(usuariosOrdenados.length, paginaUsuarios, setPaginaUsuarios)}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Barra de navegación flotante tipo dock */}
      <div style={navDockStyles.dockContainer}>
        <div style={navDockStyles.dockPill}>
          <button
            type="button"
            onClick={() => navigate('/admin')}
            style={navDockStyles.dockButton}
          >
            <div style={navDockStyles.iconWrapper}>
              <FaHome size={14} />
            </div>
            <span>Inicio</span>
          </button>

          <div style={navDockStyles.divider} />

          <button
            type="button"
            onClick={handleLogout}
            style={{ ...navDockStyles.dockButton, color: '#dc2626' }}
          >
            <div
              style={{
                ...navDockStyles.iconWrapper,
                backgroundColor: '#fee2e2',
                color: '#dc2626'
              }}
            >
              <FaSignOutAlt size={13} />
            </div>
            <span>Cerrar sesión</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReportesAdmin;

/* =========================== ESTILOS =========================== */
const styles = {
  page: {
    background: '#f8fafc',
    minHeight: '100vh',
    padding: '24px 16px 120px 16px',
    boxSizing: 'border-box',
    fontFamily: "'Poppins', sans-serif"
  },
  container: {
    maxWidth: 960,
    margin: '0 auto'
  },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16
  },
  backBtn: {
    background: '#fdf2f8',
    color: APP_PRIMARY,
    border: '1px solid #fbcfe8',
    padding: '8px 16px',
    borderRadius: 50,
    cursor: 'pointer',
    fontWeight: '600',
    fontSize: 13,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    transition: 'all 0.2s ease'
  },
  headerBadge: {
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: '0.6px',
    textTransform: 'uppercase',
    color: APP_PRIMARY,
    background: '#fdf2f8',
    padding: '4px 12px',
    borderRadius: 20
  },
  titleSection: {
    marginBottom: 24
  },
  pageTitle: {
    fontSize: 26,
    fontWeight: '700',
    color: '#1e293b',
    margin: '0 0 4px 0',
    letterSpacing: '-0.3px'
  },
  subtitle: {
    fontSize: 13,
    color: '#64748b',
    margin: 0
  },
  accordionGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 14
  },
  accordionCard: {
    background: '#ffffff',
    borderRadius: 20,
    border: '1px solid #f1f5f9',
    boxShadow: '0 4px 18px rgba(0, 0, 0, 0.03)',
    overflow: 'hidden'
  },
  accordionHeader: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 20px',
    border: 'none',
    cursor: 'pointer',
    textAlign: 'left',
    outline: 'none',
    transition: 'background-color 0.2s ease',
    fontFamily: 'inherit'
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 14
  },
  iconCircle: {
    width: 36,
    height: 36,
    borderRadius: 10,
    background: '#fdf2f8',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
  },
  sectionTitle: {
    fontSize: 15,
    fontWeight: '700',
    color: '#1e293b',
    margin: 0
  },
  sectionSubtitle: {
    fontSize: 12,
    color: '#64748b',
    display: 'block',
    marginTop: 2
  },
  chevronWrap: {
    padding: 4
  },
  accordionBody: {
    padding: '20px 24px',
    borderTop: '1px solid #f1f5f9',
    background: '#ffffff'
  },
  chartWrapper: {
    padding: '12px',
    background: '#f8fafc',
    borderRadius: 16,
    border: '1px solid #f1f5f9'
  },
  pieWrapper: {
    maxWidth: 380,
    margin: '0 auto',
    padding: '16px',
    background: '#f8fafc',
    borderRadius: 16,
    border: '1px solid #f1f5f9'
  },
  searchWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    marginBottom: 16,
    maxWidth: 400
  },
  searchIcon: {
    position: 'absolute',
    left: 14,
    color: '#94a3b8',
    fontSize: 12
  },
  inputBusqueda: {
    width: '100%',
    padding: '9px 14px 9px 38px',
    borderRadius: 50,
    border: '1.5px solid #e2e8f0',
    fontSize: 13,
    outline: 'none',
    color: '#1e293b',
    boxSizing: 'border-box'
  },
  tableWrapper: {
    overflowX: 'auto',
    borderRadius: 14,
    border: '1px solid #f1f5f9'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 13
  },
  th: {
    backgroundColor: '#f8fafc',
    padding: '12px 14px',
    textAlign: 'left',
    fontWeight: '700',
    color: '#475569',
    borderBottom: '1px solid #e2e8f0'
  },
  td: {
    padding: '12px 14px',
    borderBottom: '1px solid #f8fafc',
    color: '#334155'
  },
  rowPar: {
    backgroundColor: '#ffffff'
  },
  rowImpar: {
    backgroundColor: '#fafbfc'
  },
  emptyRow: {
    padding: '24px',
    textAlign: 'center',
    color: '#94a3b8',
    fontSize: 13
  },
  catBadge: {
    display: 'inline-block',
    background: '#f1f5f9',
    color: '#475569',
    padding: '2px 8px',
    borderRadius: 6,
    fontSize: 11,
    fontWeight: '600'
  },
  viewsBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    background: '#fdf2f8',
    color: APP_PRIMARY,
    padding: '3px 10px',
    borderRadius: 20,
    fontSize: 12,
    fontWeight: '700'
  },
  accessBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    background: '#f0fdf4',
    color: '#15803d',
    padding: '3px 10px',
    borderRadius: 20,
    fontSize: 12,
    fontWeight: '700'
  },
  paginacion: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 18,
    gap: 6
  },
  paginaBtn: {
    padding: '6px 12px',
    borderRadius: 8,
    border: '1.5px solid #e2e8f0',
    backgroundColor: '#ffffff',
    color: '#475569',
    fontSize: 12,
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  },
  paginaActiva: {
    backgroundColor: APP_PRIMARY,
    color: '#ffffff',
    borderColor: APP_PRIMARY,
    fontWeight: '700'
  }
};

const navDockStyles = {
  dockContainer: {
    position: 'fixed',
    bottom: 20,
    left: 0,
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
    pointerEvents: 'none',
    padding: '0 16px',
    boxSizing: 'border-box'
  },
  dockPill: {
    pointerEvents: 'auto',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    backgroundColor: 'rgba(255, 255, 255, 0.94)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    border: '1.5px solid rgba(241, 245, 249, 0.95)',
    borderRadius: 50,
    padding: '6px 12px',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
    transition: 'all 0.3s ease'
  },
  dockButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    border: 'none',
    borderRadius: 50,
    padding: '8px 16px',
    cursor: 'pointer',
    fontSize: '13px',
    fontFamily: "'Poppins', sans-serif",
    transition: 'all 0.2s ease',
    outline: 'none',
    backgroundColor: 'transparent',
    color: '#64748b',
    fontWeight: '600'
  },
  iconWrapper: {
    width: 28,
    height: 28,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease',
    backgroundColor: '#f1f5f9',
    color: '#64748b'
  },
  divider: {
    width: 1,
    height: 22,
    backgroundColor: '#e2e8f0',
    margin: '0 2px'
  }
};

